import React, { useEffect, useState, useMemo } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Pencil from 'lucide-react/dist/esm/icons/pencil';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import ImageIcon from 'lucide-react/dist/esm/icons/image';
import { useAmlStore } from '../../stores/amlStore';
import { PageSizeSelect, SortableHeader, TableEmptyRow } from '../shared';
import ClientForm from './ClientForm';
import type { AmlClient } from '../../types/aml';
import { SearchInput, Button, IconButton, Spinner, Pagination, ConfirmationDialog } from '../../../../components/ui';
import { useToast } from '../../../../components/feedback';
import { usePermissions, Permissions } from '../../../../hooks/usePermissions';
import { getTranslation } from '../../../../i18n';
import { useUIStore } from '../../../../stores/uiStore';

type SortDir = 'asc' | 'desc';

const ClientLogo: React.FC<{ imageName: string | null; name: string }> = ({ imageName, name }) => {
  if (imageName) {
    return (
      <img
        src={`/client-logos/${imageName}`}
        alt={name}
        className="w-20 h-11 object-contain rounded border border-subtle bg-surface-secondary"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
      />
    );
  }
  return (
    <div className="w-20 h-11 flex items-center justify-center rounded border border-subtle bg-surface-secondary text-content-muted">
      <ImageIcon size={18} />
    </div>
  );
};

const ClientList: React.FC = () => {
  const { clients, loading, fetchClients, deleteClient, showForm, setShowForm } = useAmlStore();
  const { hasPermission } = usePermissions();
  const { success, error: toastError } = useToast();
  const { language } = useUIStore();
  const tr = (key: string) => getTranslation(language, key);
  const canManage = hasPermission(Permissions.AML_CLIENTS_MANAGE);

  const [initialised, setInitialised] = useState(false);
  const [editingClient, setEditingClient] = useState<AmlClient | null>(null);
  const [deletingClient, setDeletingClient] = useState<AmlClient | null>(null);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(1);

  useEffect(() => { fetchClients().finally(() => setInitialised(true)); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (field: string, dir: SortDir) => {
    setSortField(field);
    setSortDir(dir);
    setPage(1);
  };

  const filtered = useMemo(() => {
    let result = [...clients];
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) ||
        (c.address || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q)
      );
    }
    result.sort((a, b) => {
      let va: any = (a as any)[sortField] ?? '';
      let vb: any = (b as any)[sortField] ?? '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [clients, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  useEffect(() => { setPage(1); }, [search, pageSize]);

  const handleAdd = () => { setEditingClient(null); setShowForm('client-create'); };
  const handleEdit = (e: React.MouseEvent, client: AmlClient) => { e.stopPropagation(); setEditingClient(client); setShowForm('client-edit'); };
  const handleDeleteClick = (e: React.MouseEvent, client: AmlClient) => { e.stopPropagation(); setDeletingClient(client); };
  const handleDeleteConfirm = async () => {
    if (!deletingClient) return;
    try {
      await deleteClient(deletingClient.id);
      success(tr('amlClientToastUpdated'));
    } catch (err: any) {
      toastError(err?.response?.data?.detail || tr('amlClientToastFailedToUpdate'));
    } finally {
      setDeletingClient(null);
    }
  };
  const handleFormClose = () => { setShowForm(null); setEditingClient(null); };

  const colSpan = canManage ? 5 : 4;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <SearchInput value={search} onChange={setSearch} placeholder={tr('amlClientSearchPlaceholder')} className="flex-1 max-w-sm" />
        {canManage && (
          <Button variant="success" size="lg" leftIcon={<Plus size={16} />} onClick={handleAdd}>
            {tr('amlBtnAddClient')}
          </Button>
        )}
      </div>

      <div className="rounded-xl border border-subtle bg-surface-primary overflow-hidden">
        {!initialised || loading ? (
          <div className="flex items-center justify-center h-48 text-content-muted">
            <Spinner size="lg" />
          </div>
        ) : paged.length === 0 ? (
          <TableEmptyRow title={tr('amlTableNoClientsFound')} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[600px]">
              <thead>
                <tr className="border-b border-subtle bg-surface-secondary/50">
                  <th className="px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider text-left whitespace-nowrap">{tr('amlColLogo')}</th>
                  <SortableHeader label={tr('amlColName')} field="name" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                  <SortableHeader label={tr('amlColAddress')} field="address" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                  <SortableHeader label={tr('amlColPhone')} field="phone" currentSort={sortField} currentDir={sortDir} onSort={handleSort} />
                  {canManage && (
                    <th className="w-24 text-right px-4 py-3 text-xs font-semibold text-content-muted uppercase tracking-wider">{tr('amlColActions')}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {paged.map((client) => (
                    <tr key={client.id} className="border-b border-subtle hover:bg-surface-secondary/50 transition-colors">
                      <td className="px-4 py-3">
                        <ClientLogo imageName={client.image_name} name={client.name} />
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-content-primary">{client.name}</td>
                      <td className="px-4 py-3 text-sm text-content-secondary">{client.address || '—'}</td>
                      <td className="px-4 py-3 text-sm text-content-secondary">{client.phone || '—'}</td>
                      {canManage && (
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <IconButton icon={<Pencil size={16} />} variant="ghost" aria-label={tr('amlTransactionAriaLabelEdit')} onClick={(e) => handleEdit(e, client)} />
                            <IconButton icon={<Trash2 size={16} />} variant="danger" aria-label={tr('amlTransactionAriaLabelDelete')} onClick={(e) => handleDeleteClick(e, client)} />
                          </div>
                        </td>
                      )}
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-t border-subtle bg-surface-secondary/30">
            <div className="flex items-center gap-4">
              <PageSizeSelect value={pageSize} onChange={(s) => setPageSize(s)} />
              <span className="text-sm text-content-muted whitespace-nowrap">
                {tr('amlClientShowingResults')
                  .replace('{{from}}', String((page - 1) * pageSize + 1))
                  .replace('{{to}}', String(Math.min(page * pageSize, filtered.length)))
                  .replace('{{total}}', String(filtered.length))}
              </span>
            </div>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {showForm === 'client-create' && canManage && <ClientForm client={null} onClose={handleFormClose} />}
      {showForm === 'client-edit' && editingClient && canManage && <ClientForm client={editingClient} onClose={handleFormClose} />}

      <ConfirmationDialog
        isOpen={!!deletingClient}
        onClose={() => setDeletingClient(null)}
        onConfirm={handleDeleteConfirm}
        variant="danger"
        title={tr('amlClientDeleteTitle')}
        message={tr('amlClientDeleteMessage').replace('{{name}}', deletingClient?.name ?? '')}
        confirmText={tr('amlClientDeleteConfirm')}
      />
    </div>
  );
};

export default ClientList;
